/**
 * OIDC authentication service for the admin dashboard.
 *
 * Uses oidc-client-ts to manage authentication with the platform IAM service.
 */

import { UserManager, WebStorageStateStore, User, UserManagerSettings } from 'oidc-client-ts';
import { normalizeUserRole, isSuperuserRole, isAdminRole } from '../lib/user-roles';

const IAM_ISSUER =
  import.meta.env.VITE_IDP_ISSUER ||
  import.meta.env.VITE_IAM_ISSUER ||
  'https://auth.agentic360.de';
const CLIENT_ID =
  import.meta.env.VITE_IDP_CLIENT_ID ||
  import.meta.env.VITE_IAM_CLIENT_ID ||
  'admin-dashboard@agentic360';
const PROJECT_ID =
  import.meta.env.VITE_IDP_PROJECT_ID ||
  import.meta.env.VITE_IDP_AUDIENCE ||
  import.meta.env.VITE_IAM_PROJECT_ID ||
  '';
const AUTH_REDIRECT_THROTTLE_MS = 15000;
const LAST_LOGIN_REDIRECT_KEY = 'agentic360-iam:last_login_redirect_ts';
const DEFAULT_TENANT_UUID = import.meta.env.VITE_DEFAULT_TENANT_ID || '00000000-0000-0000-0000-000000000001';
const SCHULEN_TENANT_UUID = 'a1b2c3d4-e5f6-7890-abcd-ef1234567890';
const AGENTIC_ORG_ID =
  import.meta.env.VITE_IAM_AGENTIC360_ORG_ID ||
  import.meta.env.VITE_NEXT_PUBLIC_IAM_AGENTIC360_ORG_ID || '';
const SCHULEN_ORG_ID =
  import.meta.env.VITE_IAM_SCHULEN_ORG_ID ||
  import.meta.env.VITE_NEXT_PUBLIC_IAM_SCHULEN_ORG_ID || '';

const ORG_TO_TENANT_UUID: Record<string, string> = {};
if (AGENTIC_ORG_ID) {
  ORG_TO_TENANT_UUID[String(AGENTIC_ORG_ID)] = DEFAULT_TENANT_UUID;
}
if (SCHULEN_ORG_ID && !ORG_TO_TENANT_UUID[String(SCHULEN_ORG_ID)]) {
  ORG_TO_TENANT_UUID[String(SCHULEN_ORG_ID)] = SCHULEN_TENANT_UUID;
}

function buildAuthorizationScope(projectId: string): string {
  const scopes = ['openid', 'email', 'profile', 'offline_access'];
  void projectId;
  return scopes.join(' ');
}

const authSettings: UserManagerSettings = {
  authority: IAM_ISSUER,
  client_id: CLIENT_ID,
  redirect_uri: `${window.location.origin}/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  scope: buildAuthorizationScope(PROJECT_ID),
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  // Keep transient OAuth state outside the tab-scoped session store so the
  // callback still resolves if the browser restores the redirect in a new context.
  stateStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true,
  silent_redirect_uri: `${window.location.origin}/silent-renew.html`,
  // Explicit endpoint configuration keeps the admin bundle independent from discovery quirks.
  metadata: {
    issuer: IAM_ISSUER,
    authorization_endpoint: `${IAM_ISSUER}/oauth/v2/authorize`,
    token_endpoint: `${IAM_ISSUER}/oauth/v2/token`,
    userinfo_endpoint: `${IAM_ISSUER}/oidc/v1/userinfo`,
    end_session_endpoint: `${IAM_ISSUER}/oidc/v1/end_session`,
    jwks_uri: `${IAM_ISSUER}/oauth/v2/keys`,
    revocation_endpoint: `${IAM_ISSUER}/oauth/v2/revoke`,
    introspection_endpoint: `${IAM_ISSUER}/oauth/v2/introspect`,
  },
};

// Create UserManager instance
export const userManager = new UserManager(authSettings);

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function extractOrgIdFromProjectRoles(payload: Record<string, unknown>): string | undefined {
  for (const [key, value] of Object.entries(payload)) {
    if (!key.startsWith('urn:zitadel:iam:org:project:') || !key.endsWith(':roles')) {
      continue;
    }
    if (!value || typeof value !== 'object') {
      continue;
    }

    const rolesObj = value as Record<string, unknown>;
    for (const roleValue of Object.values(rolesObj)) {
      if (!roleValue || typeof roleValue !== 'object') {
        continue;
      }
      const orgIds = Object.keys(roleValue as Record<string, unknown>);
      if (orgIds.length > 0) {
        return orgIds[0];
      }
    }
  }
  return undefined;
}

function extractRolesFromProjectClaims(payload: Record<string, unknown>): string[] {
  const roles = new Set<string>();

  const addRoles = (value: unknown) => {
    if (!value || typeof value !== 'object') {
      return;
    }

    for (const role of Object.keys(value as Record<string, unknown>)) {
      roles.add(normalizeUserRole(role));
    }
  };

  addRoles(payload['urn:zitadel:iam:org:project:roles']);

  for (const [key, value] of Object.entries(payload)) {
    if (!key.startsWith('urn:zitadel:iam:org:project:') || !key.endsWith(':roles')) {
      continue;
    }
    addRoles(value);
  }

  return Array.from(roles);
}

function hasRealmAccessRoles(payload: Record<string, unknown>): boolean {
  const realmAccess = payload.realm_access as { roles?: unknown } | undefined;
  return Array.isArray(realmAccess?.roles) && realmAccess.roles.length > 0;
}

function hasTenantOrRoleClaims(payload: Record<string, unknown>): boolean {
  return Boolean(
    extractTenantIdFromPayload(payload)
    || extractRolesFromProjectClaims(payload).length > 0
    || hasRealmAccessRoles(payload)
  );
}

export function extractTenantIdFromPayload(payload: Record<string, unknown>): string | undefined {
  const customTenantId = payload.tenant_id;
  if (typeof customTenantId === 'string' && customTenantId.trim()) {
    return customTenantId;
  }

  const orgIdClaim = payload['urn:zitadel:iam:org:id'];
  const orgId = typeof orgIdClaim === 'string' || typeof orgIdClaim === 'number' ? String(orgIdClaim) : undefined;
  if (orgId && ORG_TO_TENANT_UUID[orgId]) {
    return ORG_TO_TENANT_UUID[orgId];
  }

  const orgIdFromRoles = extractOrgIdFromProjectRoles(payload);
  if (orgIdFromRoles && ORG_TO_TENANT_UUID[orgIdFromRoles]) {
    return ORG_TO_TENANT_UUID[orgIdFromRoles];
  }

  if (orgId && isUuid(orgId)) {
    return orgId;
  }

  return undefined;
}

function getLastRedirectTimestamp(): number {
  const raw = window.sessionStorage.getItem(LAST_LOGIN_REDIRECT_KEY);
  const parsed = raw ? Number(raw) : 0;
  if (!Number.isFinite(parsed) || parsed < 0) {
    window.sessionStorage.removeItem(LAST_LOGIN_REDIRECT_KEY);
    return 0;
  }
  return parsed;
}

function markLoginRedirect(): void {
  window.sessionStorage.setItem(LAST_LOGIN_REDIRECT_KEY, String(Date.now()));
}

function clearLoginRedirectMark(): void {
  window.sessionStorage.removeItem(LAST_LOGIN_REDIRECT_KEY);
}

userManager.events.addSilentRenewError((error) => {
  console.error('[IAM Auth] Silent renew error:', error);
});

/**
 * Initiates the login flow by redirecting to the configured OIDC provider.
 */
export async function login(): Promise<void> {
  if (window.location.pathname === '/callback') {
    return;
  }

  const now = Date.now();
  const lastRedirect = getLastRedirectTimestamp();
  if (now - lastRedirect < AUTH_REDIRECT_THROTTLE_MS) {
    console.warn('[IAM Auth] Throttling repeated login redirect to avoid auth loop');
    return;
  }

  markLoginRedirect();
  try {
    // Preserve the deep link the user actually requested so the callback can
    // return them there instead of always landing on /dashboard.
    const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    await userManager.signinRedirect({ state: { returnUrl } });
  } catch (error) {
    clearLoginRedirectMark();
    throw error;
  }
}

/**
 * Handles the OAuth callback after the provider redirects back.
 * Returns the authenticated User object.
 */
export async function handleCallback(): Promise<User> {
  try {
    return await userManager.signinRedirectCallback();
  } finally {
    clearLoginRedirectMark();
  }
}

/**
 * Initiates the logout flow by redirecting to the provider end_session endpoint.
 * Falls back to clearing local state and redirecting to origin if OIDC logout fails.
 */
export async function logout(): Promise<void> {
  try {
    await userManager.signoutRedirect();
  } catch {
    // OIDC end_session failed or timed out — clear local state and hard-redirect.
    userManager.clearStaleState();
    await userManager.removeUser();
    sessionStorage.clear();
    window.location.href = window.location.origin;
  }
}

/**
 * Returns the currently authenticated user, or null if not authenticated.
 */
export async function getUser(): Promise<User | null> {
  const user = await userManager.getUser();
  if (!user || user.expired || !user.profile) {
    return user;
  }

  const payload = user.profile as Record<string, unknown>;
  if (hasTenantOrRoleClaims(payload)) {
    return user;
  }

  try {
    const renewedUser = await userManager.signinSilent();
    return renewedUser ?? user;
  } catch {
    return user;
  }
}

/**
 * Returns a valid access token, attempting silent renewal if expired.
 * Returns undefined if no valid token is available.
 */
export async function getAccessToken(): Promise<string | undefined> {
  const user = await userManager.getUser();
  if (user && !user.expired) {
    return user.access_token;
  }
  // Try silent renew
  try {
    const renewed = await userManager.signinSilent();
    return renewed?.access_token;
  } catch {
    return undefined;
  }
}

/**
 * Extracts user roles from the OIDC user profile.
 * Supports both compatible project-role claims and backwards-compatible realm_access format.
 *
 * Native IAM preserves the legacy project-role claim
 * or in project-scoped claims such as `urn:zitadel:iam:org:project:<project-id>:roles`.
 */
export function getUserRoles(user: User | null): string[] {
  if (!user?.profile) {
    return [];
  }

  const projectRoles = extractRolesFromProjectClaims(user.profile as Record<string, unknown>);
  if (projectRoles.length > 0) {
    return projectRoles;
  }

  // Also check the access token claims (may contain roles from Actions).
  // Access token is stored as id_token_claims in oidc-client-ts.
  const idTokenClaims = user.profile as Record<string, unknown>;
  const tokenProjectRoles = extractRolesFromProjectClaims(idTokenClaims);
  if (tokenProjectRoles.length > 0) {
    return tokenProjectRoles;
  }

  // Backwards-compatible realm_access claim shape.
  const realmAccess = user.profile['realm_access'] as { roles?: string[] } | undefined;
  if (realmAccess?.roles) {
    return Array.from(
      new Set(realmAccess.roles.map(normalizeUserRole)),
    );
  }

  return [];
}

/**
 * Extracts the tenant_id from the authenticated user profile custom claims.
 * Falls back to the default tenant UUID if not present.
 */
export function getUserTenantId(user: User | null): string {
  if (!user?.profile) return DEFAULT_TENANT_UUID;
  const tenantId = extractTenantIdFromPayload(user.profile as Record<string, unknown>);
  return tenantId || DEFAULT_TENANT_UUID;
}

/**
 * Returns the highest-priority role for the user.
 * Priority: superadmin > admin > tenant-admin > user
 */
export function getUserPrimaryRole(user: User | null): string {
  const roles = getUserRoles(user);

  if (roles.some(isSuperuserRole)) {
    return 'superadmin';
  }
  if (roles.some(isAdminRole)) {
    return 'admin';
  }
  if (roles.includes('tenant-admin') || roles.includes('tenant_admin')) {
    return 'tenant-admin';
  }
  return 'user';
}

/**
 * Gets the current authentication state.
 * Returns true if user is authenticated with a valid, non-expired token.
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    const user = await userManager.getUser();
    return user !== null && !user.expired;
  } catch {
    return false;
  }
}

/**
 * Clears the local authentication state without calling the IdP.
 * Useful for handling auth errors locally.
 */
export async function clearAuthState(): Promise<void> {
  clearLoginRedirectMark();
  await userManager.removeUser();
}
